const STORAGE_KEY = 'system-management-verification';
export const SYSTEM_ACCESS_EXPIRED = 'system-management-verification-expired';

interface VerificationSession {
  token: string;
  expiresAt: string;
  loginToken: string;
  pageId?: string;
  pathname?: string;
}

// Memory is used for requests; sessionStorage resumes only the same page after
// a server status check, with the original absolute expiration deadline.
let verification: VerificationSession | null = null;

export function readSystemAccess(): VerificationSession | null {
  if (verification?.token && verification.loginToken === localStorage.getItem('token') &&
      Date.parse(verification.expiresAt) > Date.now()) return verification;
  verification = null;
  return null;
}

export function saveSystemAccess(data: { token: string; expiresAt: string }, page?: { pageId: string; pathname: string }) {
  verification = { ...data, ...page, loginToken: localStorage.getItem('token') || '' };
  try {
    if (page) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(verification));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* Storage can be unavailable; verification still works in memory. */ }
}

export function readSavedSystemAccess(): VerificationSession | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    const login = localStorage.getItem('token');
    if (login && saved?.loginToken === login && typeof saved.token === 'string' && saved.token &&
        typeof saved.pageId === 'string' && saved.pageId && typeof saved.pathname === 'string' &&
        isSystemPath(saved.pathname) && Date.parse(saved.expiresAt) > Date.now()) return saved;
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* Missing/invalid stored state requires password verification. */ }
  return null;
}

export function discardSystemAccess(keepSavedPage = false) {
  verification = null;
  if (!keepSavedPage) {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* No storage is needed. */ }
  }
}

export function clearSystemAccess(message = '') {
  discardSystemAccess();
  window.dispatchEvent(new CustomEvent(SYSTEM_ACCESS_EXPIRED, { detail: message }));
}

// Navigation to another management page and explicit locking revoke the old
// page. A refresh preserves its remaining time; expiry is still server-enforced.
export function revokeSystemPage(pageId: string, loginToken: string) {
  const base = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : (process.env.API_BASE_URL || '');
  void fetch(`${base}/api/v1/system-access/revoke`, {
    method: 'POST', keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${loginToken}` },
    body: JSON.stringify({ pageId }),
  }).catch(() => { /* The server's expiry remains a fallback when offline. */ });
}

export function isSystemPath(pathname: string) {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/system/cloak-whitelist' || path === '/cloak/self-check') return false;
  return path === '/system' || path.startsWith('/system/');
}
